/**
 * React Query hooks for roles and role groups.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  fetchRoleGroups,
  createRoleGroup,
  updateRoleGroup,
  deleteRoleGroup,
} from '@/services/roles.service';
import {
  fetchRoleInterfaceBindings,
  replaceRoleInterfaceBindings,
} from '@/services/interface-elements.service';
import type {
  CreateRolePayload,
  UpdateRolePayload,
  CreateRoleGroupPayload,
  UpdateRoleGroupPayload,
  RoleBinding,
} from '@/types/role.types';

/** React Query key for the unpaginated roles list. */
const ROLES_KEY = ['roles'] as const;
/** React Query key for role groups. */
const ROLE_GROUPS_KEY = ['roles', 'groups'] as const;
/** React Query key for one role's interface bindings. */
const ROLE_BINDINGS_KEY = (roleId: string) => ['roles', roleId, 'bindings'] as const;

/** `GET /roles` — unpaginated role list for pickers. */
export const useRoles = () => {
  return useQuery({
    queryKey: ROLES_KEY,
    queryFn: fetchRoles,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/** `GET /roles/:id` */
export const useRole = (id: string | null | undefined) => {
  return useQuery({
    queryKey: [...ROLES_KEY, id],
    queryFn: () => getRole(id!),
    enabled: !!id,
  });
};

/** `POST /roles` — invalidates the roles list. */
export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRolePayload) => createRole(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
};

/** `PUT /roles/:id` — invalidates the roles list. */
export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRolePayload }) =>
      updateRole(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
};

/** `DELETE /roles/:id` — invalidates the roles list. */
export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
};

/** `GET /role-groups` */
export const useRoleGroups = () => {
  return useQuery({ queryKey: ROLE_GROUPS_KEY, queryFn: fetchRoleGroups });
};

/** `POST /role-groups` — invalidates role-group lists. */
export const useCreateRoleGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoleGroupPayload) => createRoleGroup(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROLE_GROUPS_KEY });
    },
  });
};

/** `PUT /role-groups/:id` — invalidates role-group lists. */
export const useUpdateRoleGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoleGroupPayload }) =>
      updateRoleGroup(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROLE_GROUPS_KEY });
    },
  });
};

/** `DELETE /role-groups/:id` — invalidates role-group lists. */
export const useDeleteRoleGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRoleGroup(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROLE_GROUPS_KEY });
    },
  });
};

/** `GET /roles/:roleId/interface-bindings` */
export const useRoleInterfaceBindings = (roleId: string | null | undefined) => {
  return useQuery({
    queryKey: roleId ? ROLE_BINDINGS_KEY(roleId) : ['roles', 'bindings', 'disabled'],
    queryFn: () => fetchRoleInterfaceBindings(roleId!),
    enabled: !!roleId,
  });
};

/** `PUT /roles/:roleId/interface-bindings` — replace all bindings; invalidates bindings + roles. */
export const useReplaceRoleBindings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, bindings }: { roleId: string; bindings: RoleBinding[] }) =>
      replaceRoleInterfaceBindings(roleId, bindings),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ROLE_BINDINGS_KEY(variables.roleId) });
      void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
};
